import cloneDeep from "lodash/cloneDeep";
import { Abstract_Attribute } from "../../constants";
import { DataContext } from "../../types/data.types";
import { Data } from "../Data";
import { EmployeeAttributeList } from "../attributes/EmployeeAttributeList";
import { GapAttributeList } from "../attributes/GapAttributeList";
import { JobAttributeList } from "../attributes/JobAttributeList";
import { UserAttributeSummary } from "./UserAttributeSummary";

export interface ProfileEmployeeData {
  employeeId: string;
  employeeNumber?: string;
  gender?: string;
  email?: string;
  fullName?: string;
  jobTitle?: string;
  location?: string;
  preferredName?: string;
  ethnicity?: string;
  isOrganizationAdmin?: boolean;
  isTeamLead?: boolean;
  reportsTo?: string | null;
  hasAGroupManagerApprover?: boolean;
  startDate?: string;
  locationName?: string;
  dateOfBirth?: string;
  organizationName?: string;
  employeeAttributes?: unknown[];
  attributeSummaryDto?: unknown;
}

export interface ProfileJobDefinitionData {
  description?: string;
  jobAttributes?: AttributeWithTask[];
  regulationTasks?: RegulationTask[];
}

export interface ProfileJobData {
  department?: string;
}

export interface RegulationTask {
  id: string;
  name: string;
  competencies?: { attributeDefinitionId: string }[];
}

export interface ProfileApiResponse {
  employee: ProfileEmployeeData;
  jobDefinition?: ProfileJobDefinitionData;
  gapAttributes?: unknown[];
  job?: ProfileJobData | null;
}

export interface UserProfileOptions {
  employeeId: string;
  resolve: (profile: UserProfile) => void;
  reject: (error: unknown) => void;
  context: DataContext;
  postData?: Record<string, unknown>;
}

export interface TaskAttributeTreeItem {
  id: string;
  name?: string;
  attributeHierarchy: string[];
  missing?: boolean;
  rowId: string;
  isTask?: boolean;
  inTask?: boolean;
  attributeDefinitionId?: string;
}

export interface AttributeWithTask {
  id?: string;
  attributeDefinitionId: string;
  inTask: boolean;
  missing?: boolean;
  mandatory?: boolean;
  required?: boolean;
  type?: string;
  attributeHierarchy?: string[];
  rowId?: string;
  currentProficiency?: number | null;
  gapReason?: number | { label: string };
  childAttributes?: AttributeWithTask[];
  requiredProficiency?: number;
  name?: string;
}

export class UserProfile extends Data<ProfileApiResponse> {
  employeeId!: string;
  employeeNumber!: string;
  gender!: string;
  email!: string;
  fullName!: string;
  jobTitle!: string;
  location!: string;
  preferredName!: string;
  ethnicity!: string;
  isAdmin!: boolean;
  reportsTo!: string | null;
  hasManager!: boolean;
  hasGroupManager!: boolean;
  hasApprover!: boolean;
  startDate!: string;
  locationName!: string;
  dateOfBirth!: string;
  organizationName!: string;
  employeeAttributes!: EmployeeAttributeList;
  gapAttributes!: GapAttributeList;
  attributeSummary!: UserAttributeSummary;
  jobDescription!: string;
  jobAttributes!: JobAttributeList | null;
  hasJob!: boolean;
  department!: string;

  constructor(options: UserProfileOptions) {
    super({
      api: "/Profile?employeeId=" + options.employeeId,
      callback: (data) => {
        const { employee, jobDefinition, gapAttributes, job } = data;

        this.employeeId = employee.employeeId;
        this.employeeNumber = employee.employeeNumber || "";
        this.gender = employee.gender || "";
        this.email = employee.email || "";
        this.fullName = employee.fullName || "";
        this.jobTitle = employee.jobTitle || "";
        this.location = employee.location || "";
        this.preferredName = employee.preferredName || "";
        this.ethnicity = employee.ethnicity || "";
        this.isAdmin = employee.isOrganizationAdmin || employee.isTeamLead || false;
        this.reportsTo = employee.reportsTo || null;
        this.hasManager = this.reportsTo !== null;
        this.hasGroupManager = employee.hasAGroupManagerApprover || false;
        this.hasApprover = this.hasManager === true || this.hasGroupManager === true;
        this.startDate = employee.startDate || "";
        this.locationName = employee.locationName || "";
        this.dateOfBirth = employee.dateOfBirth || "";
        this.organizationName = employee.organizationName || "";

        this.employeeAttributes = new EmployeeAttributeList({ data: employee.employeeAttributes });

        this.gapAttributes = new GapAttributeList({ data: gapAttributes });
        this.attributeSummary = new UserAttributeSummary(employee.attributeSummaryDto);
        //Job
        this.jobAttributes = null;
        if (jobDefinition) {
          this.jobDescription = jobDefinition.description || "";
          this.jobAttributes = new JobAttributeList({ data: jobDefinition.jobAttributes });
          this.updateJobAttributes();
          this.gapAttributes.initAttributesLists(this.jobAttributes, this.employeeAttributes);
          this.generateTaskAttributesTree(jobDefinition);
        }

        //Job
        this.hasJob = job !== null && job !== undefined;
        if (this.hasJob && job) {
          this.department = job.department || "";
        }

        options.resolve(this);
      },
      ...options
    });
  }

  onDataReceived(_data: ProfileApiResponse): void { }

  static getUserProfile(context: DataContext, employeeId: string): Promise<UserProfile> {
    return new Promise((resolve, reject) => {
      new UserProfile({ employeeId: employeeId, resolve, reject, context });
    });
  }

  generateTaskAttributesTree(jobDefinition: ProfileJobDefinitionData): void {
    if (!this.jobAttributes) return;

    const jobAttributes = this.jobAttributes.attributes;
    const employeeAttrs = this.employeeAttributes.flatAttributes.filter(
      (attr: AttributeWithTask) =>
        !!jobAttributes.find(
          (jobAttr: AttributeWithTask) =>
            jobAttr.attributeDefinitionId === attr.attributeDefinitionId
        )
    );
    const gapAttrs = this.gapAttributes.flatAttributes;
    const taskAttributesTree: TaskAttributeTreeItem[] = [];

    const attributesMap: Record<string, AttributeWithTask> = {};
    [...gapAttrs, ...employeeAttrs].forEach((_attr: AttributeWithTask) => {
      const attr = cloneDeep(_attr) as AttributeWithTask;
      attr.inTask = false;
      if (!attributesMap[attr.attributeDefinitionId]) {
        attributesMap[attr.attributeDefinitionId] = attr;
      }
    });

    jobAttributes.forEach((attr: AttributeWithTask) => {
      const foundAttr = attributesMap[attr.attributeDefinitionId];
      //Employee can have attributes that are not in job definition
      if (foundAttr) {
        foundAttr.missing = attr.missing;
        foundAttr.mandatory = attr.mandatory;
        foundAttr.required = attr.required;
        foundAttr.type = attr.type;
      } else {
        //Desirable attributes that are not in employee attributes
        const _attr = cloneDeep(attr) as AttributeWithTask;
        _attr.missing = attr.missing;
        _attr.inTask = false;
        attributesMap[_attr.attributeDefinitionId] = _attr;
      }
    });
    const attributesInTask: TaskAttributeTreeItem[] = [];
    const regulationTasks = jobDefinition.regulationTasks ?? [];
    regulationTasks.forEach((task: RegulationTask) => {
      const attributes =
        task.competencies
          ?.map(({ attributeDefinitionId }) => attributesMap[attributeDefinitionId])
          ?.filter((_attr): _attr is AttributeWithTask => Boolean(_attr))
          ?.map((_attr) => {
            _attr.inTask = true;
            const attr = cloneDeep(_attr) as AttributeWithTask;
            attr.attributeHierarchy = [task.id, attr.id ?? ""];
            attr.rowId = `${task.id}_${attr.id ?? ""}`;
            return attr as TaskAttributeTreeItem;
          }) ?? [];
      attributesInTask.push(...attributes);

      const taskWithAttributes: TaskAttributeTreeItem = {
        id: task.id,
        name: task.name,
        attributeHierarchy: [task.id],
        missing: attributes.some((attribute) => attribute.missing),
        rowId: task.id,
        isTask: true
      };
      taskAttributesTree.push(taskWithAttributes);
    });
    taskAttributesTree.push(...attributesInTask);
    const attributesNotInTask = Object.values(attributesMap).filter(
      (attr) => !attr.inTask
    ) as unknown as TaskAttributeTreeItem[];
    taskAttributesTree.push(...attributesNotInTask);
    this.jobAttributes.taskAttributesTree = taskAttributesTree;

    const flat =
      jobDefinition.jobAttributes
        ?.filter((attribute) =>
          Object.prototype.hasOwnProperty.call(attributesMap, attribute.attributeDefinitionId)
        )
        ?.map(
          ({ attributeDefinitionId }: { attributeDefinitionId: string }) =>
            attributesMap[attributeDefinitionId]
        )
        ?.map((_attr) => {
          const attr = cloneDeep(_attr) as AttributeWithTask;
          attr.attributeHierarchy = [attr.id ?? ""];
          return attr;
        }) ?? [];

    this.jobAttributes.flatAttributes = flat;
  }

  updateJobAttributes(): void {
    if (!this.jobAttributes) return;

    this.jobAttributes.attributes.forEach((a: AttributeWithTask) => {
      a.currentProficiency = this.getProficiency(a.attributeDefinitionId);
      a.missing = a.gapReason !== Abstract_Attribute.gapReason.met; //emplAttr === undefined;

      if (a.childAttributes)
        a.childAttributes.forEach((c: AttributeWithTask) => {
          const emplChildAttr = this.jobAttributes?.getByDefinition(c.attributeDefinitionId);
          if (emplChildAttr) c.gapReason = emplChildAttr.gapReason;

          c.missing = c.gapReason !== Abstract_Attribute.gapReason.met;
          //if(c.missing) a.missing = true;
        });
    });
  }

  removeAttribute(attr: AttributeWithTask): void {
    this.employeeAttributes.removeAttribute(attr);
  }

  getProficiency(jobDefId: string): number | null {
    const attr = this.employeeAttributes.getByDefinition(jobDefId);
    return attr ? (attr.currentProficiency ?? null) : null;
  }

  isComplient(attr: AttributeWithTask | null): boolean | null {
    if (!attr) return false;

    const reqProf = attr.requiredProficiency ?? 0;
    const curProd = this.getProficiency(attr.attributeDefinitionId) ?? 0;
    const profMet = curProd >= reqProf;

    return attr.mandatory ? profMet : profMet ? true : null;
  }

  list: AttributeWithTask[] = [];

  findMatch(attrName: string): AttributeWithTask | undefined {
    return this.list.find((a: AttributeWithTask) => a.name === attrName);
  }

  hasAttribute(attr: { attributeDefinitionId: string }): unknown {
    return this.employeeAttributes?.getAttributeByEmployeeAttributeId(attr.attributeDefinitionId);
  }
}
